import dynamic from 'next/dynamic';
import AbsoluteSkewebar from "@/components/Common/AbsoluteSkewebar";
import AnimatedTitleHeader from "@/components/TitleHeader/AnimatedTitleHeader";
import Chip from "@/components/chip";
import Link from "next/link";
import React, { useState } from "react";
import { useRouter } from "next/router";
import he from "he";
import RenderChildrenBasedOnScreen from "@/components/Common/RenderChildrenBasedOnScreen";
import Grid from "@mui/material/Grid";
import MetaTags from "@/components/seo/meta-tags";
import Placeholder from "@/components/placeholder";

// Lazy load Card component (used multiple times below-the-fold)
const Card = dynamic(() => import('@/components/blogs/Card'));

const NO_OF_BLOGS_IN_MOBILE = 3;

const Blogs = ({ data }) => {
  const { data: blogs, category_list, category_blogs } = data;
  const [blogState, setBlogState] = useState({
    currentSelectedCategory: 0,
  });

  const router = useRouter();

  const { currentSelectedCategory } = blogState;

  const handleCategoryChange = (categoryIndex, category) => {
    setBlogState({
      ...blogState,
      currentSelectedCategory: categoryIndex,
    });
    if (category && category.slug) {
      router.push(`/blogs/${he.decode(category.slug)}`);
    }
  };

  const handleBlogClick = (blog) => {
    let category = blog?.yoast_head_json?.schema["@graph"][0].articleSection[0];

    if (blog && blog.slug && category) {
      category = category.replace(/ /g, "-").toLowerCase();
      const match = blog?.yoast_head_json?.canonical.match(/\/wp\/(.+?)\/?$/);
      if (match[1]) {
        router.push(`/blogs/${match[1]}`);
      } else {
        router.push(`/blogs/${he.decode(category)}/${he.decode(blog.slug)}`);
      }
    }
  };

  return (
    <>
      <MetaTags
        title="Elchemy Blog: Market Analysis, Innovations & Chemical Sourcing"
        description="Stay updated with Elchemy's blog on chemical insights, sourcing trends, and market innovations. Explore in-depth reports and industry-leading strategies today."
      />
      {blogs?.length ? (
        <>
          <main className="overflow-hidden">
            <section>
              <AnimatedTitleHeader
                text={"Our Blogs"}
                imgSrc={
                  "https://storage.googleapis.com/elchemy-dev/1706090369_78qTslG.svg"
                }
                titleAsH1
              />
            </section>
            <section className="px-[2rem] sm:px-[4.4rem] md:px-[6.75rem]  pt-4 sm:pt-6 md:pt-12">
              <div className=" top-[4rem] ">
                <p className=" mt-1 ">
                  <Link
                    href={"/"}
                    passHref={true}
                    className="text-[0.85rem] text-[#808080]"
                  >
                    Home
                  </Link>{" "}
                  <span className="px-1">/</span>{" "}
                  <span className="font-medium text-[0.9rem]">Blogs</span>
                </p>
              </div>

              <RenderChildrenBasedOnScreen
                showOnMobile={true}
                showOnTablet={false}
              >
                <div className="overflow-x-auto scrollbar-hide mt-4">
                  <div className="flex items-center gap-3 pb-4 whitespace-nowrap">
                    {category_list?.map((category, index) => (
                      <div key={index} className="flex-shrink-0">
                        <Chip
                          onClick={() => handleCategoryChange(index, category)}
                          label={category?.name}
                          selected={currentSelectedCategory === index}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </RenderChildrenBasedOnScreen>

              <RenderChildrenBasedOnScreen
                showOnMobile={false}
                showOnTablet={true}
              >
                <div className="flex flex-wrap gap-[15px] gap-y-[16px] items-center mx-auto mt-[45px]">
                  {category_list?.map((category, index) => {
                    return (
                      <Chip
                        onClick={() => handleCategoryChange(index, category)}
                        key={index}
                        label={category?.name}
                        selected={currentSelectedCategory == index}
                      />
                    );
                  })}
                </div>
              </RenderChildrenBasedOnScreen>

              {blogs?.length ? (
                <div className="sm:mt-[60px]">
                  <div className="flex justify-between mb-3">
                    <AbsoluteSkewebar text={"Featured Blogs"} />
                  </div>
                  <Grid container spacing={3}>
                    {blogs?.map((blog, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        className={
                          index > NO_OF_BLOGS_IN_MOBILE - 1
                            ? "hidden sm:block"
                            : ""
                        }
                      >
                        <Card
                          blog={blog}
                          onClick={() => handleBlogClick(blog)}
                          width={false}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </div>
              ) : (
                <></>
              )}

              {category_blogs?.length ? (
                category_blogs?.map((category, index) => {
                  return (
                    <div key={index} className="sm:mt-[60px]">
                      <div className="flex justify-between mb-3 items-center">
                        <AbsoluteSkewebar text={category?.name} />
                        <Link
                          href={`/blogs/${category?.slug}`}
                          className="whitespace-nowrap text-[#EF4136] cursor-pointer"
                        >
                          View All
                        </Link>
                      </div>
                      <Grid container spacing={3}>
                        {category?.blogs?.map((blog, index) => (
                          <Grid key={index} item xs={12} sm={6} md={4}>
                            <Card
                              key={index}
                              blog={blog}
                              onClick={() => handleBlogClick(blog)}
                              width={false}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  );
                })
              ) : (
                <></>
              )}
            </section>
          </main>
        </>
      ) : (
        <Placeholder title="No Blogs Available" />
      )}
    </>
  );
};

export async function getServerSideProps() {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_BASE_URL}/blogs/?per_page=6`
  );
  const response = await res.json();
  if (response?.success) {
    return {
      props: {
        data: response,
      },
    };
  } else {
    return {
      props: {
        data: {
          data: [],
          category_list: [],
          category_blogs: [],
        },
      },
    };
  }
}

export default Blogs;
